import { Injectable, inject } from '@angular/core';

import assert from 'tiny-invariant';

import { NumberToWordsService } from '@ts/shared/util-number-to-words';

import { LOCALE } from './money.constant';
import { Currency, Money, MoneyDisplay, Sign } from './money.model';

@Injectable({
  providedIn: 'root',
})
export class MoneyFormatService {
  private numberToWordsService = inject(NumberToWordsService);

  CURRENCY_TO_DISPLAY: { [currency in Currency]: string } = {
    IDR: 'Rp',
  };
  CURRENCY_TO_WORDS: { [currency in Currency]: string } = {
    IDR: 'rupiah',
  };

  getMoneyDisplay(money: Money): MoneyDisplay {
    const splitted = money.amount.split('.');
    assert([1, 2].includes(splitted.length));

    let integerPart = Number(splitted[0]);
    let sign: Sign;
    if (integerPart < 0) {
      sign = '-';
      integerPart = -integerPart;
    } else {
      sign = '';
    }

    return {
      sign,
      currency: this.CURRENCY_TO_DISPLAY[money.currency],
      amount: new Intl.NumberFormat(LOCALE).format(integerPart),
      decimal: splitted.length === 2 ? splitted[1] : '00',
      decimalSeparator: ',',
    };
  }

  /**
   * @example
   * ```
   * formatMoney({currency: 'IDR', amount: '-15000.00'}) == 'Rp -15.000,00'
   * ```
   */
  formatMoney(money: Money): string {
    // We don't want to call formatMoneyNoCurrency to avoid calling getMoneyDisplay twice.
    const display = this.getMoneyDisplay(money);
    return `${display.currency}\xa0${display.sign}${display.amount}${display.decimalSeparator}${display.decimal}`;
  }

  /**
   * @example
   * ```
   * formatMoney({currency: 'IDR', amount: '-15000.00'}) == '-15.000,00'
   * ```
   */
  formatMoneyNoCurrency(money: Money): string {
    const display = this.getMoneyDisplay(money);
    return `${display.sign}${display.amount}${display.decimalSeparator}${display.decimal}`;
  }

  toWords(money: Money): string {
    return `${this.numberToWordsService.toCardinal(Number(money.amount))} ${this.CURRENCY_TO_WORDS[money.currency]}`;
  }
}
